import React, { useState, useEffect } from 'react';
import { X, Edit3, Trash2, MessageCircle, User, Calendar, Heart, Share, Flag, Save, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface PostManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  report: any;
  onRefresh?: () => void;
}

const PostManagementModal: React.FC<PostManagementModalProps> = ({
  isOpen,
  onClose,
  reportId,
  report,
  onRefresh
}) => {
  const [post, setPost] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    if (isOpen && report) {
      fetchPostDetails();
    }
  }, [isOpen, report]);

  // Safety check to prevent rendering errors
  if (!isOpen || !report) return null;

  const fetchPostDetails = async () => {
    try {
      setLoading(true);
      const resp = await api.get(`/admin/moderation/reports/${reportId}`);
      if (resp.data?.success && resp.data.data.report.reportedPost) {
        const postData = resp.data.data.report.reportedPost;
        setPost(postData);
        setEditedContent(postData?.content || '');
        setComments(Array.isArray(postData?.comments) ? postData.comments : []);
      }
    } catch (error: any) {
      console.error('Error fetching post details:', error);
      toast.error(error?.response?.data?.error?.message || 'Failed to load post details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editedContent.trim()) {
      toast.error('Post content cannot be empty');
      return;
    }
    try {
      const resp = await api.put(`/posts/${post._id}`, {
        content: editedContent.trim(),
        moderationEdit: true,
        moderationReason: 'Content edited by admin for policy compliance',
      });
      if (resp.data?.success) {
        setPost({ ...post, content: editedContent.trim() });
        setEditingPost(false);
        toast.success('Post updated successfully and user notified');
      } else {
        toast.error(resp.data?.error?.message || 'Failed to update post');
      }
    } catch (error: any) {
      console.error('Error updating post:', error);
      toast.error(error?.response?.data?.error?.message || 'Failed to update post');
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) return;
    try {
      const resp = await api.post(`/admin/moderation/reports/${reportId}/delete-post`, {
        reason: 'Deleted by admin via post management',
        notifyUser: true,
      });
      if (resp.data?.success) {
        toast.success('Post deleted successfully');
        onRefresh?.();
        onClose();
      } else {
        toast.error(resp.data?.error?.message || 'Failed to delete post');
      }
    } catch (error: any) {
      console.error('Error deleting post:', error);
      toast.error(error?.response?.data?.error?.message || 'Failed to delete post');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((comments || []).filter(c => c._id !== commentId));
      toast.success('Comment deleted successfully');
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      toast.error(error?.response?.data?.error?.message || 'Failed to delete comment');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }
    try {
      setAddingComment(true);
      const resp = await api.post(`/comments`, {
        post: post._id,
        content: `[ADMIN] ${newComment.trim()}`,
      });
      if (resp.data?.success) {
        setComments([...(comments || []), resp.data.data.comment]);
        setNewComment('');
        toast.success('Admin comment added successfully');
      } else {
        toast.error(resp.data?.error?.message || 'Failed to add comment');
      }
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast.error(error?.response?.data?.error?.message || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getInitials = (name: string) => {
    return name.split(' ').map((n, index) => n[0]).join('').toUpperCase();
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Flag className="w-6 h-6 text-red-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Post Management</h2>
              <p className="text-sm text-gray-500">
                Reported by {report?.reporter?.name} • {report?.reason}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Report Information */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-medium text-red-900 mb-2">Report Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-red-800">Reason:</span>
                  <span className="ml-2 text-red-700">{report?.reason}</span>
                </div>
                <div>
                  <span className="font-medium text-red-800">Priority:</span>
                  <span className="ml-2 text-red-700">{report?.priority}</span>
                </div>
                <div>
                  <span className="font-medium text-red-800">Reporter:</span>
                  <span className="ml-2 text-red-700">{report?.reporter?.name}</span>
                </div>
                <div>
                  <span className="font-medium text-red-800">Reported:</span>
                  <span className="ml-2 text-red-700">{formatDate(report?.createdAt)}</span>
                </div>
              </div>
              {report?.description && (
                <div className="mt-3">
                  <span className="font-medium text-red-800">Description:</span>
                  <p className="text-red-700 mt-1">{report.description}</p>
                </div>
              )}
            </div>

            {/* Post Information */}
            {post && post.author && (
              <div className="bg-white border border-gray-200 rounded-lg">
                {/* Post Header */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-medium">
                        {post.author?.avatar ? (
                          <img src={post.author.avatar} alt={post.author.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          getInitials(post.author?.name || 'U')
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{post.author?.name}</h3>
                        <p className="text-sm text-gray-500">{formatDate(post.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingPost(!editingPost)}
                        className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                        <span>{editingPost ? 'Cancel' : 'Edit'}</span>
                      </button>
                      <button
                        onClick={handleDeletePost}
                        className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-4">
                  {editingPost ? (
                    <div className="space-y-3">
                      <textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Edit post content..."
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveEdit}
                          className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          <span>Save Changes</span>
                        </button>
                        <button
                          onClick={() => {
                            setEditingPost(false);
                            setEditedContent(post.content);
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-900 whitespace-pre-wrap">{post.content}</p>
                  )}
                </div>

                {/* Post Images */}
                {post.images && post.images.length > 0 && (
                  <div className="px-4 pb-4">
                    <div className="grid grid-cols-2 gap-2">
                      {post.images.map((image: any, index: number) => (
                        <img
                          key={index}
                          src={image.url}
                          alt={`Post image ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Post Stats */}
                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex space-x-4">
                      <span className="flex items-center space-x-1">
                        <Heart className="w-4 h-4" />
                        <span>{Array.isArray(post.likes) ? post.likes.length : (post.likes || 0)} likes</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <MessageCircle className="w-4 h-4" />
                        <span>{(comments || []).length} comments</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Share className="w-4 h-4" />
                        <span>{Array.isArray(post.shares) ? post.shares.length : (post.shares || 0)} shares</span>
                      </span>
                    </div>
                    <span>Sport: {post.sport || 'General'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Comments ({(comments || []).length})</h3>
              </div>

              {/* Add Admin Comment */}
              <div className="p-4 border-b border-gray-200 bg-blue-50">
                <div className="flex space-x-3">
                  <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-medium">
                    A
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add an admin comment..."
                      className="w-full h-20 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={addingComment || !newComment.trim()}
                      className="mt-2 flex items-center space-x-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{addingComment ? 'Adding...' : 'Add Admin Comment'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Comments List */}
              <div className="max-h-64 overflow-y-auto">
                {(comments || []).length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No comments yet
                  </div>
                ) : (
                  (comments || []).map((comment) => (
                    <div key={comment._id} className="p-4 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-start justify-between">
                        <div className="flex space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gray-500 text-white flex items-center justify-center text-sm font-medium">
                            {comment.author?.avatar ? (
                              <img src={comment.author.avatar} alt={comment.author.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              getInitials(comment.author?.name || 'U')
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{comment.author?.name}</span>
                              <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                            </div>
                            <p className="text-gray-700 mt-1">{comment.content}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteComment(comment._id)}
                          className="p-1 text-red-400 hover:text-red-600 transition-colors"
                          title="Delete comment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostManagementModal;